import React, { useState } from 'react'
import { FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa'
import { Badge, Heading, Text, Box, FormControl, Input, Textarea, Select, Stack, Button, Icon, EmptyBox, ConfirmButton, SimpleModal, SimpleTable, Wrap, WrapItem, Card } from '@/components/lib'

import api from '@/api'
import utils from '@/utils/utils'
import useStore from '@/store'
import useTitle from '@/hooks/useTitle'

export default function StageGroups () {
  const { user, trial, stages, stageGroups, addStageGroup, deleteStageGroup } = useStore()
  // Stage-group CRUD is gated on trial.write on the API (can_write_trial).
  const canEdit = utils.canWriteTrial(user, trial)
  useTitle('Stage Groups', trial)

  function onDelete (groupId) {
    api.stageGroups.delete(groupId, () => {
      utils.toast('success', 'Stage group deleted')
      deleteStageGroup(groupId)
    }, err => utils.toast('error', 'Unable to delete stage group', err.message))
  }

  function move (s, direction) {
    const newIndex = (s.index || 0) + direction
    if (newIndex < 0 || newIndex >= stageGroups.length) return
    api.stageGroups.update(s._id, { index: newIndex }, () => {
      api.stageGroups.listForTrial(trial._id, d => d.stageGroups.forEach(addStageGroup))
    })
  }

  const totalStageGroups = stageGroups?.length || 0
  return (
    <Stack gap={5}>
      <Stack direction='row' justifyContent='space-between'>
        <Box>
          <Heading as='h2' size='lg'>Stage groups</Heading>
          <Text>Collections of stages for improved visit coordination or other management.</Text>
        </Box>
        {canEdit && <StageGroupCreator />}
      </Stack>

      {(stageGroups?.length > 0)
        ? (
          <SimpleTable
            asCard
            headers={['', 'Name', 'Stages', '']}
            rows={stageGroups.map(s => {
              const groupStages = []
              s.stages?.forEach(gs => {
                const stage = stages.find(st => st._id === gs.stage)
                groupStages.push({ _id: stage?._id, name: stage?.name, recurrence: gs.recurrence, recurrenceName: utils.recurrenceCustomName(stage, gs.recurrence) })
              })
              return [
                <Stack key={s._id} w='30px'>
                  {canEdit && s.index > 0 && <Button size='xs' onClick={e => move(s, -1)}><Icon as={FaArrowUp} /></Button>}
                  {canEdit && s.index < (totalStageGroups - 1) && <Button size='xs' onClick={e => move(s, 1)}><Icon as={FaArrowDown} /></Button>}
                </Stack>,
                <Stack key={s._id}>
                  <Heading as='h3' size='md'>{s.name}</Heading>
                  <Text fontSize='sm' color='fg.muted'>{s.description}</Text>
                </Stack>,
                <Wrap gap={3} key={s._id}>
                  {groupStages.map(gs =>
                    <WrapItem key={`${gs._id}-${gs.recurrence}`}>
                      <Badge colorPalette='blue'>{gs.name} {gs.recurrenceName ? `(${gs.recurrenceName})` : gs.recurrence ? `(${gs.recurrence + 1})` : ''}</Badge>
                    </WrapItem>)}
                </Wrap>,
                <Stack direction='row' justifyContent='end' gap={2} key={s._id}>
                  {canEdit &&
                    <>
                      <StageGroupCreator stageGroup={s} trigger={<Button size='sm' colorPalette='primary'>Edit</Button>} />
                      <ConfirmButton
                        header='Really delete this stage group?'
                        trigger={<Button size='sm' variant='ghost'><Icon><FaTrash /></Icon></Button>}
                        onConfirm={() => onDelete(s._id)}
                      >Delete
                      </ConfirmButton>
                    </>}
                </Stack>
              ]
            })}
          />)
        : (
          <EmptyBox icon='groupbubbles' title={canEdit ? 'Create your first stage group' : 'No stage groups'} description='Stage groups are collections of forms you can use to make participant visits easier.'>
            {canEdit &&
              <StageGroupCreator
                trigger={<Button colorPalette='primary'>Create your first stage group</Button>}
              />}
          </EmptyBox>)}
    </Stack>
  )
}

function StageGroupCreator ({ stageGroup, trigger }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(stageGroup?.name || '')
  const [description, setDescription] = useState(stageGroup?.description || '')
  const [selectedStageId, setSelectedStageId] = useState('')
  const [selectedStageRecurrence, setSelectedStageRecurrence] = useState('')
  const [groupStages, setGroupStages] = useState(stageGroup?.stages || [])
  const { trial, addStageGroup, stages } = useStore()
  const isEditing = !!stageGroup

  function save () {
    const data = { name, description, stages: groupStages }
    if (isEditing) {
      api.stageGroups.update(stageGroup._id, data, updated => {
        addStageGroup(updated)
        setOpen(false)
      }, err => utils.toast('error', 'Unable to update stage group', err.message))
    } else {
      api.stageGroups.create(trial._id, data, created => {
        addStageGroup(created)
        setOpen(false)
      }, err => utils.toast('error', 'Unable to create stage group', err.message))
    }
  }

  function addStage () {
    if (groupStages.find(gs => gs.stage === selectedStageId)) { return utils.toast('error', 'Each stage can only be added to a group one time') }
    setGroupStages([...groupStages, {
      stage: selectedStageId,
      recurrence: selectedStageRecurrence === '' ? null : selectedStageRecurrence
    }])
    setSelectedStageId('')
    setSelectedStageRecurrence('')
  }

  const selectedStage = stages.find(s => s._id === selectedStageId)

  return (
    <>
      {trigger
        ? React.cloneElement(trigger, { onClick: () => setOpen(true) })
        : <Button colorPalette='primary' onClick={() => setOpen(true)}>Create new stage group</Button>}
      <SimpleModal
        isOpen={open} onClose={() => setOpen(false)} size='lg'
        header={isEditing ? 'Edit your stage group' : 'Create a new stage group'}
        body={
          <Stack gap={5}>
            <FormControl label='Name' required>
              <Input value={name} onChange={e => setName(e.target.value)} />
            </FormControl>
            <FormControl label='Description'>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} />
            </FormControl>

            <Card gap={3}>
              <Heading as='h3' size='md'>Stages in this group</Heading>

              {groupStages?.length === 0 &&
                <EmptyBox icon='form' title='No stages in this group' description='Add stages to this group using the controls below.' />}
              {groupStages?.length > 0 &&
                <SimpleTable
                  headers={['Stage', 'Recurrence', '']}
                  rows={groupStages.map(s => {
                    const stage = stages.find(st => st._id === s.stage)
                    return [
                      stage?.name,
                      s.recurrence !== null ? utils.recurrenceLabel(stage, s.recurrence) : '',
                      <Button key={s.stage} size='sm' variant='ghost' onClick={() => setGroupStages(groupStages.filter(gs => gs !== s))}><Icon><FaTrash /></Icon></Button>
                    ]
                  })}
                />}
              <Stack direction='row'>
                <Select
                  placeholder='Select a stage' onChange={e => {
                    setSelectedStageId(e.target.value)
                    setSelectedStageRecurrence('')
                  }} value={selectedStageId}
                >
                  {stages.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </Select>
                {selectedStage?.isRecurring &&
                  <Select placeholder='Select recurrence' onChange={e => setSelectedStageRecurrence(parseInt(e.target.value))} value={selectedStageRecurrence}>
                    {Array.from({ length: selectedStage.recurringDuration }, (_, i) => i).map(i =>
                      <option key={i} value={i}>{utils.recurrenceLabel(selectedStage, i)}</option>)}
                  </Select>}
                <Button onClick={addStage}>Add stage</Button>
              </Stack>
            </Card>
          </Stack>
}
        footer={
          <Stack direction='row' justifyContent='flex-end'>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button colorPalette='primary' onClick={save}>Save</Button>
          </Stack>
}
      />
    </>
  )
}
